import { components } from "@/api-schema";
import { RelocationOrderTransportMethod } from "@/api-schema-enums";
import {
  RelocationOrderFlowRouteType,
  RelocationOrderFlowTransportMethodSegment,
} from "./default-relocation-order-flow.enums";

type RelocationOrder = components["schemas"]["RelocationOrder"];

export enum RelocationOrderFlowAction {
  TransportCompanySetPickupDate = "transport-company-set-pickup-date",
  BranchApproveReadyForTransport = "branch-approve-ready-for-transport",
  TransportCompanyPickupVehicle = "transport-company-pickup-vehicle",
  TransportCompanyRejectVehicle = "transport-company-reject-vehicle",
  TransportCompanyUnloadVehicle = "transport-company-unload-vehicle",
  BranchAcceptVehicle = "branch-accept-vehicle",
  BranchRejectVehicle = "branch-reject-vehicle",
  BranchResolveTransportCompanyRejectedVehicle = "branch-resolve-transport-company-rejected-vehicle",
  BranchResolveBranchRejectedVehicle = "branch-resolve-branch-rejected-vehicle",
  BranchComplete = "branch-complete",
}

export type RelocationOrderFlowActionBodyMap = {
  [RelocationOrderFlowAction.TransportCompanySetPickupDate]: components["schemas"]["FitSetPickupDateBody"];
  [RelocationOrderFlowAction.BranchApproveReadyForTransport]: never;
  [RelocationOrderFlowAction.TransportCompanyPickupVehicle]: never;
  [RelocationOrderFlowAction.TransportCompanyRejectVehicle]: components["schemas"]["TransportCompanyRejectVehicleBody"];
  [RelocationOrderFlowAction.TransportCompanyUnloadVehicle]: components["schemas"]["TransportCompanyUnloadVehicleBody"];
  [RelocationOrderFlowAction.BranchAcceptVehicle]: never;
  [RelocationOrderFlowAction.BranchRejectVehicle]: components["schemas"]["BranchRejectVehicleBody"];
  [RelocationOrderFlowAction.BranchResolveTransportCompanyRejectedVehicle]: components["schemas"]["BranchResolveTransportCompanyRejectedVehicleBody"];
  [RelocationOrderFlowAction.BranchResolveBranchRejectedVehicle]: components["schemas"]["BranchResolveBranchRejectedVehicleBody"];
  [RelocationOrderFlowAction.BranchComplete]: never;
};

/** Mutation input: just the relocation order for bodyless actions, relocation order + body for actions with a body. */
export type RelocationOrderFlowActionInput<A extends RelocationOrderFlowAction> =
  RelocationOrderFlowActionBodyMap[A] extends never
    ? RelocationOrderFlowResolvable
    : { relocationOrder: RelocationOrderFlowResolvable; body: RelocationOrderFlowActionBodyMap[A] };

/** Minimal shape needed to resolve the flow path for a relocation order. */
export type RelocationOrderFlowResolvable = Pick<
  RelocationOrder,
  "uuid" | "originBranch" | "destinationBranch" | "transportMethod"
>;

function resolveRouteType(relocationOrder: RelocationOrderFlowResolvable): RelocationOrderFlowRouteType {
  const hasOriginBranch = relocationOrder.originBranch !== null;
  const hasDestinationBranch = relocationOrder.destinationBranch !== null;

  if (hasOriginBranch && hasDestinationBranch) return RelocationOrderFlowRouteType.BranchToBranch;
  if (hasOriginBranch && !hasDestinationBranch) return RelocationOrderFlowRouteType.BranchToExternalAddress;
  if (!hasOriginBranch && hasDestinationBranch) return RelocationOrderFlowRouteType.ExternalAddressToBranch;

  throw new Error(
    `Cannot resolve flow route type: originBranch=${relocationOrder.originBranch}, destinationBranch=${relocationOrder.destinationBranch}`,
  );
}

function resolveTransportMethodSegment(
  relocationOrder: RelocationOrderFlowResolvable,
): RelocationOrderFlowTransportMethodSegment {
  switch (relocationOrder.transportMethod) {
    case RelocationOrderTransportMethod.TransportCompany:
      return RelocationOrderFlowTransportMethodSegment.TransportCompany;
    case RelocationOrderTransportMethod.OwnAxle:
      return RelocationOrderFlowTransportMethodSegment.OwnAxle;
    default:
      throw new Error(`Unknown transport method: ${relocationOrder.transportMethod}`);
  }
}

/**
 * Resolves the API path prefix for a relocation order's flow variant.
 *
 * @example
 * resolveRelocationOrderFlowPathPrefix(relocationOrder) → "/v1/relocation-order/flow/branch-to-branch/transport-company"
 */
export function resolveRelocationOrderFlowPathPrefix(relocationOrder: RelocationOrderFlowResolvable): string {
  const routeType = resolveRouteType(relocationOrder);
  const method = resolveTransportMethodSegment(relocationOrder);

  return `/v1/relocation-order/flow/${routeType}/${method}`;
}

/**
 * Resolves the full API path for a specific flow action on a relocation order.
 *
 * @example
 * resolveRelocationOrderFlowActionPath(relocationOrder, "branch-accept-vehicle")
 *   → "/v1/relocation-order/flow/branch-to-branch/transport-company/{uuid}/branch-accept-vehicle"
 */
export function resolveRelocationOrderFlowActionPath(
  relocationOrder: RelocationOrderFlowResolvable,
  action: RelocationOrderFlowAction,
): string {
  const prefix = resolveRelocationOrderFlowPathPrefix(relocationOrder);
  return `${prefix}/${relocationOrder.uuid}/${action}`;
}
